import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, ReplaySubject, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpRequestResponse } from '../../lib/models/http-request-response.model';
import { HttpRequestService } from '../../lib/services/http-request.service';
import { LogService } from '../../lib/services/log.service';
import { UtenteService } from '../../lib/services/utente.service';

import { Router } from '@angular/router';
import { FunzionalitaService } from '../../lib/services/funzionalita.service';
import { newError } from '../../lib/utils/Utility';
import { Notifica } from '../types/Notifica';
import { RispostaAvvisi } from '../types/Avviso';
import { RispostaRichieste } from '../types/Richiesta';

@Injectable({
  providedIn: 'root'
})
export class ComunicazioniService {
  private httpRequestService = inject(HttpRequestService);
  private logService = inject(LogService);
  private utenteService = inject(UtenteService);
  private funzionalitaService = inject(FunzionalitaService);
  router = inject(Router);


  public lastComunicationsCount = new ReplaySubject<number>(1)
  private avvisiNonLettiSubject = new BehaviorSubject<number>(0);
  public readonly avvisiNonLetti$ = this.avvisiNonLettiSubject.asObservable();
  private richiesteNonLetteSubject = new BehaviorSubject<number>(0);
  public readonly richiesteNonLette$ = this.richiesteNonLetteSubject.asObservable();

  inviaComunicazione(notifica: Partial<Notifica>) {
    const url = '/siev_mnotifiche?token=' + this.utenteService.getToken();
    return from(this.httpRequestService.sendFile(url, { notifica }));
  }

  estraiAlberoComunicazione(id: number) {
    return this.httpRequestService.get('/notifica/risposte-tutte-sct', { id })
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            return response.data.rows;
          } else {
            this.logService.messageError('Attenzione', response.message, newError(response.message));
            return null;
          }
        })
      )
  }

  comunicazioneRicorsivaReinvia(id: string) {
    return this.impostaFlagComunicazioneRicorsiva(id, 'S');
  }

  comunicazioneRicorsivaIgnora(id: string) {
    return this.impostaFlagComunicazioneRicorsiva(id, 'N');
  }

  private impostaFlagComunicazioneRicorsiva(id: string, reinvia: string) {
    return this.httpRequestService.post('/notifica/farmaco/reinvia', { id_notifica: id, reinvia })
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            return response;
          } else {
            this.logService.messageError('Attenzione', response.message, newError(response.message));
            return null;
          }
        })
      );
  }

  impostaNotificaLetta(id: number, showSpinner: boolean = false) {
    return this.httpRequestService.post('/siev_fsetview_notifica', { id: id }, {}, showSpinner)
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            return response;
          } else {
            this.logService.messageError('Attenzione', response.message, newError(response.message));
            return null;
          }
        })
      );
  }

  estraiAvvisi(limit: number = 50, soloNonLetti: boolean = false) {
    return this.httpRequestService.post('/notifica/avvisi', { limit, soloNonLetti }, {}, false)
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            const result = response.data as RispostaAvvisi;
            this.impostaNumeroAvvisiNonLetti(result?.nonLetti || 0);
            return result;
          }

          this.logService.messageError('Attenzione', response.message, newError(response.message));
          return null;
        })
      );
  }

  aggiornaAvvisiNonLetti() {
    return this.estraiAvvisi(1).pipe(
      map(result => result?.nonLetti || 0)
    );
  }

  segnaAvvisiComeLetti(codguids: string[]) {
    if (codguids.length === 0) {
      return from(Promise.resolve(0));
    }

    return this.httpRequestService.post('/notifica/avvisi/letti', { codguids: JSON.stringify(codguids) }, {}, false)
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            const nonLetti = Number(response.data?.nonLetti || 0);
            this.impostaNumeroAvvisiNonLetti(nonLetti);
            return nonLetti;
          }

          this.logService.messageError('Attenzione', response.message, newError(response.message));
          return null;
        })
      );
  }

  estraiRichieste(limit: number = 50, soloNonLetti: boolean = false) {
    return this.httpRequestService.post('/notifica/richieste', { limit, soloNonLetti }, {}, false)
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            const result = response.data as RispostaRichieste;
            this.impostaNumeroRichiesteNonLette(result?.nonLetti || 0);
            return result;
          }

          this.logService.messageError('Attenzione', response.message, newError(response.message));
          return null;
        })
      );
  }

  aggiornaRichiesteNonLette() {
    return this.estraiRichieste(1).pipe(
      map(result => result?.nonLetti || 0)
    );
  }

  segnaRisposteRichiesteComeLette(ids: number[]) {
    if (ids.length === 0) {
      return from(Promise.resolve(0));
    }

    return this.httpRequestService.post('/notifica/richieste/letti', { ids: JSON.stringify(ids) }, {}, false)
      .pipe(
        map(response => {
          if (HttpRequestResponse.isSuccessResponse(response)) {
            const nonLetti = Number(response.data?.nonLetti || 0);
            this.impostaNumeroRichiesteNonLette(nonLetti);
            return nonLetti;
          }

          this.logService.messageError('Attenzione', response.message, newError(response.message));
          return null;
        })
      );
  }

  cancellaRichiesta(idNotifica: number) {
    return this.httpRequestService.delete('/notifica/richieste/' + idNotifica, {}, {}, false);
  }

  private impostaNumeroAvvisiNonLetti(value: number) {
    this.avvisiNonLettiSubject.next(Math.max(0, Number(value) || 0));
  }

  private impostaNumeroRichiesteNonLette(value: number) {
    this.richiesteNonLetteSubject.next(Math.max(0, Number(value) || 0));
  }
}
